import { config } from '../Components/Config';

const addJWTToHeaders = (options = {}) => {
  const crmUserData = localStorage.getItem('crmUser');
  const crmUser = crmUserData ? JSON.parse(crmUserData) : {};
  const token = crmUser?.token || localStorage.getItem('jwtToken');

  // FormData needs the browser to set its own Content-Type with a generated
  // boundary — forcing application/json here breaks multer's multipart parsing.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Drop-in replacement for fetch() — use exactly like the native fetch()
export const jwtFetch = async (endpoint, options = {}) => {
  if (isTokenExpiringSoon()) {
    await refreshToken();
  }
  const headers = addJWTToHeaders(options);
  return fetch(endpoint, { ...options, headers });
};

// Alias for jwtFetch, kept for backward compatibility
export const apiRequest = jwtFetch;

// Combines jwtFetch with automatic JSON parsing
export const apiCall = async (endpoint, options = {}) => {
  const response = await jwtFetch(endpoint, options);
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const data = await response.json();
    return { response, data };
  } else {
    const text = await response.text();
    return { response, data: { text } };
  }
};

// Calls the backend to mint a fresh token; used proactively by jwtFetch before expiry
export const refreshToken = async () => {
  try {
    const crmUserData = localStorage.getItem('crmUser');
    const crmUser = crmUserData ? JSON.parse(crmUserData) : {};

    if (!crmUser.user_keyid) {
      return false;
    }

    const response = await fetch(config.ENDPOINT + 'refresh/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_keyid: crmUser.user_keyid }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();

    if (data.status === 200 && data.data?.token) {
      const updatedUser = {
        ...crmUser,
        token: data.data.token,
        token_expires_at: data.data.token_expires_at,
      };

      localStorage.setItem('crmUser', JSON.stringify(updatedUser));
      localStorage.setItem('jwtToken', data.data.token);

      return true;
    }

    return false;

  } catch (error) {
    return false;
  }
};

export const isTokenExpiringSoon = () => {
  try {
    const crmUserData = localStorage.getItem('crmUser');
    const crmUser = crmUserData ? JSON.parse(crmUserData) : {};

    if (!crmUser.token_expires_at) {
      return true; // No token, consider it expired
    }

    const expiresAt = new Date(crmUser.token_expires_at);
    const now = new Date();
    const minutesUntilExpiry = (expiresAt - now) / 1000 / 60;

    return minutesUntilExpiry < 5;

  } catch (error) {
    console.error('[API] Error checking token expiry:', error);
    return true; // Assume expired if error
  }
};

// For debugging only — not used in any request/auth logic
export const getTokenInfo = () => {
  try {
    const crmUserData = localStorage.getItem('crmUser');
    const crmUser = crmUserData ? JSON.parse(crmUserData) : {};

    return {
      hasToken: !!crmUser.token,
      user_keyid: crmUser.user_keyid,
      email: crmUser.email_address,
      expires_at: crmUser.token_expires_at,
      expiring_soon: isTokenExpiringSoon(),
    };
  } catch (error) {
    console.error('[API] Error getting token info:', error);
    return { hasToken: false, error: error.message };
  }
};

export default {
  jwtFetch,
  apiRequest,
  apiCall,
  refreshToken,
  isTokenExpiringSoon,
  getTokenInfo,
};
